// Imports for external libraries go here.
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import Glide, { Static } from '@glidejs/glide';
import DOMPurify from 'isomorphic-dompurify';
import { ResponsiveGrid, EditableComponent } from '@adobe/aem-react-editable-components';
import { Button, respGridUtil, useWindowSize } from '@marriott/mi-ui-library';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import {
  MerchandisingCarouselContainerProps,
  ICarouselSlidesInAuthorMode,
  ICarouselControls,
} from './MerchandisingCarouselContainer.types';
import {
  StyledCarouselList,
  StyledMerchandisingCarouselContainer,
  StyledPostCardHeroCarouselList,
} from './MerchandisingCarouselContainer.styles';
import { CAROUSEL, getDirection, CAROUSEL_VARIATION } from '../../utils/helpers';

// Use named rather than default exports.
export const MerchandisingCarouselContainerNonEditable: FC<MerchandisingCarouselContainerProps> = props => {
  const {
    headerText,
    cardDescription,
    ctaLabel,
    ctaLink,
    noOfCards,
    ctaType,
    cardCount,
    openInaNewTab,
    totalNumberOfCards,
    trackingProperties,
    cqItems,
    componentId,
    isAuthorMode,
    noOfCardsTablet,
    pagePath,
    itemPath,
    styleclass,
    nextLabel,
    prevLabel,
    variation,
    isHide = false,
  } = props;
  const isPostCardHeroVariation = variation == CAROUSEL_VARIATION.POST_CARD_HERO;
  const [cardsPerView, setCardsPerView] = useState(noOfCards);
  const [isAutoplayPaused, setIsAutoplayPaused] = useState(false);
  const [autoplayInterval, setAutoplayInterval] = useState(20000);
  const isAutoplayPausedRef = useRef<boolean>(false);
  const [updatedCardCount, setUpdatedCardCount] = useState(cardCount);
  const [isLoaded, setIsLoaded] = useState(isAuthorMode);
  const glideRef = useRef<Static | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const sanitizedNode = DOMPurify.sanitize(isLoaded ? cardDescription : '', { RETURN_DOM: true }) as HTMLElement;
  const fragmentString = sanitizedNode.innerHTML;

  const ArrowDisabler = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (Glide: any, Components: any) => {
      return {
        mount() {
          // Only in effect when rewinding is disabled
          // if (Glide.settings.rewind) {
          //   return;
          // }
          Glide.on(['mount.after', 'run'], () => {
            if (Components.Sizes.length != updatedCardCount) {
              setUpdatedCardCount(Components.Sizes.length);
            }
            const carousel = carouselRef.current?.querySelector(`#${componentId}__slides`);
            carousel?.classList?.remove('serverStyles');
            // Filter out arrows_control
            for (const controlItem of Components.Controls.items) {
              if (controlItem.className !== 'glide__arrows') {
                continue;
              }

              // Set left arrow state
              const left = controlItem?.querySelector('.glide__arrow--left');
              const direction = getDirection();
              if (left) {
                if (direction === 'rtl' && Glide.index === Components.Sizes.length - Glide.settings.perView) {
                  left?.classList?.add('.is-disabled'); // Disable on first slide
                  left?.setAttribute('disabled', 'true');
                } else if (direction === 'ltr' && Glide.index === 0) {
                  left?.classList.add('.is-disabled'); // Disable on first slide
                  left?.setAttribute('disabled', 'true');
                } else {
                  left?.classList.remove('.is-disabled'); // Enable on other slides
                  left?.removeAttribute('disabled');
                }
              }

              // Set right arrow state
              const right = controlItem?.querySelector('.glide__arrow--right');
              if (right) {
                if (direction === 'rtl' && Glide.index === 0) {
                  right?.classList.add('.is-disabled'); // Disable on last slide
                  right?.setAttribute('disabled', 'true');
                } else if (direction === 'ltr' && Glide.index === Components.Sizes.length - Glide.settings.perView) {
                  right?.classList.add('.is-disabled');
                  right?.setAttribute('disabled', 'true');
                } else {
                  right?.classList.remove('.is-disabled'); // Disable on other slides
                  right?.removeAttribute('disabled');
                }
              }
            }
          });
        },
      };
    },
    [componentId]
  );

  const updateCarouselArrowsPlacement = () => {
    if (isPostCardHeroVariation) {
      setCardsPerView(1);
      return;
    }
    if (window?.matchMedia('(max-width: 576px)')?.matches) {
      setCardsPerView(1);
    } else if (window?.matchMedia('(max-width: 992px)')?.matches) {
      setCardsPerView(noOfCardsTablet);
    } else {
      setCardsPerView(noOfCards);
    }
  };

  const handleAutoplayToggle = useCallback(() => {
    if (!isPostCardHeroVariation || !glideRef.current) return;
    if (isAutoplayPaused) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (glideRef.current as any)?.play();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (glideRef.current as any)?.pause();
    }
    isAutoplayPausedRef.current = !isAutoplayPaused;
    setIsAutoplayPaused(prev => !prev);
  }, [isAutoplayPaused, isPostCardHeroVariation]);

  const onResize = () => {
    const isMobile = window.innerWidth <= 767;
    if (isMobile) {
      isAutoplayPausedRef.current = true;
      setIsAutoplayPaused(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (glideRef.current as any)?.pause();
    } else {
      isAutoplayPausedRef.current = false;
      setIsAutoplayPaused(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (glideRef.current as any)?.play(autoplayInterval);
    }
  };

  useEffect(() => {
    if (totalNumberOfCards && totalNumberOfCards?.length && !isAuthorMode) {
      const postCardHeroGlideConfig = {
        type: 'carousel',
        perView: 1,
        peek: 142,
        focusAt: 'center',
        gap: 24,
        direction: 'ltr',
        autoplay: 20000,
        rewind: true,
        breakpoints: {
          // Tablet settings screen <= 992px
          992: {
            gap: 24,
            peek: 78,
            autoplay: 20000,
          },
          // mobile settings screen <= 767px
          767: {
            gap: 16,
            peek: 38,
            autoplay: false,
          },
          9000: {
            gap: 24,
            peek: 142,
            autoplay: 20000,
          },
        },
      } as const;

      const defaultConfig = {
        type: 'slider',
        perView: noOfCards,
        peek: { before: 0, after: 0 },
        startAt: 0,
        gap: 16,
        dragThreshold: false,
        direction: 'ltr',
        bound: true,
        rewind: false,
        breakpoints: {
          992: {
            perView: noOfCardsTablet,
            peek: {
              before: 0,
              after: 0,
            },
            bound: true,
          },
          576: {
            perView: 1,
            peek: {
              before: 0,
              after: 0,
            },
          },
          90000: {
            perView: noOfCards,
          },
        },
        keyboard: false,
      } as const;

      const glide = new Glide(`#${componentId}`, isPostCardHeroVariation ? postCardHeroGlideConfig : defaultConfig);
      glideRef.current = glide;
      if (!isPostCardHeroVariation) {
        glide.on(['resize', 'build.before'], updateCarouselArrowsPlacement);
        const glideProps = glide?.mount({ ArrowDisabler });
        glide.on('resize', () => {
          const noOfItems = carouselRef.current?.querySelectorAll(`.${CAROUSEL.ITEM}`)?.length || 0;
          if (noOfItems > glideProps?.settings?.perView) glideProps?.go('=0');
        });
      } else {
        glide?.mount();
        const carousel = carouselRef.current?.querySelector(`#${componentId}__slides`);
        carousel?.classList?.remove('serverStyles');
        setCardsPerView(1);

        glide.on('resize', onResize);
      }
    }
  }, []);

  useEffect(() => {
    const pageWcmMode = document.getElementsByTagName('html')[0];
    const updateStyleComponent = document.getElementById(`${componentId}__slides`);
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const currWcmMode = pageWcmMode?.getAttribute('class');
          if (currWcmMode?.includes('Edit')) {
            updateStyleComponent?.classList.add('glide__slides_authoring');
          } else if (currWcmMode?.includes('Preview')) {
            updateStyleComponent?.classList.remove('glide__slides_authoring');
          }
        }
      });
    });
    observer.observe(pageWcmMode, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, [componentId]);

  const UpdateCarousel = (event?: unknown) => {
    setUpdatedCardCount(totalNumberOfCards?.length - Number(carouselRef.current?.getAttribute('data-deleted-count')));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (glideRef.current as any)?.update({
      autoplay: isPostCardHeroVariation
        ? (event as CustomEvent<{ isMathVariation: boolean }>)?.detail?.isMathVariation
          ? 40000
          : 20000
        : false,
    });
    if ((event as CustomEvent<{ isMathVariation: boolean }>)?.detail?.isMathVariation) {
      setAutoplayInterval(40000);
    }
    setIsLoaded(true);
  };

  useEffect(() => {
    carouselRef.current?.addEventListener(CAROUSEL.EVENT, UpdateCarousel);
    if (carouselRef.current?.getAttribute('data-carousel-updated') === 'true') UpdateCarousel();
    return () => {
      carouselRef.current?.removeEventListener(CAROUSEL.EVENT, UpdateCarousel);
    };
  }, []);

  useEffect(() => {
    if (isLoaded && glideRef.current && isPostCardHeroVariation && window?.innerWidth <= 767) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (glideRef.current as any)?.update({
        autoplay: false,
      });
    }
  }, [isLoaded]);

  const CarouselList = getCarouselList(variation);
  return (
    <StyledMerchandisingCarouselContainer
      id={CAROUSEL.ID}
      data-testid="merch-MerchandisingCarouselContainer"
      data-component-name="o-merch-MerchandisingCarouselContainer"
      className={clsx(variation, styleclass, styleclass?.includes('fullbleed') ? 'm-container-fullbleed' : '', {
        'd-none ': isHide,
      })}
      ref={carouselRef}
      defaultValue={cardsPerView}
      data-cardsPerView={cardsPerView}
      data-count={0}
      data-total={totalNumberOfCards?.length}
      data-carousel-updated={'false'}
      data-deleted-count={0}
      data-math-variation={'false'}
    >
      <div
        className={clsx({
          'container px-3': !isPostCardHeroVariation || isAuthorMode,
          'container-full ': isPostCardHeroVariation && !isAuthorMode,
          'px-md-0': !styleclass?.includes('fullbleed'),
          'd-none': Number(updatedCardCount) <= 0,
        })}
      >
        <div className="d-flex justify-content-between mb-4">
          <div className={clsx({ 'col-6 px-0': !isLoaded })}>
            {headerText && (
              <h2 className={clsx('t-title-s mb-2 m-ellipsis-2lines', { 'skeleton-loader-composite py-4': !isLoaded })}>
                {isLoaded && headerText}
              </h2>
            )}
            {cardDescription && (
              <div
                dangerouslySetInnerHTML={{ __html: fragmentString }}
                className={clsx('card-description t-font-m mb-0 m-ellipsis-2lines', {
                  'skeleton-loader-composite py-3': !isLoaded,
                })}
              />
            )}
          </div>
          {ctaLabel && (
            <div
              className={clsx('carousal_header--cta d-flex align-self-end', {
                'skeleton-loader-composite py-3 col-3 px-0': !isLoaded,
              })}
            >
              {isLoaded && (
                <Button
                  href={ctaLink}
                  isLink={ctaType === 'tertiaryLink'}
                  target={openInaNewTab ? '_blank' : '_self'}
                  callback={() => {
                    window?.open(ctaLink, openInaNewTab ? '_blank' : '_self');
                  }}
                  className={[
                    '',
                    ctaType === 'primaryButton' ? 'm-button-primary' : '',
                    ctaType === 'secondaryButton' ? 'm-button-secondary' : '',
                    ctaType === 'tertiaryLink' ? 'm-link-tertiary-button' : '',
                    openInaNewTab
                      ? ctaType === 'primaryButton' || ctaType === 'secondaryButton'
                        ? 'm-button-external'
                        : 'm-link-tertiary-button-external'
                      : '',
                  ]}
                  trackingProperties={trackingProperties}
                  buttonCopy={ctaLabel}
                  isTextBeforeChildren={false}
                />
              )}
            </div>
          )}
        </div>

        <div id={componentId} className="glide" style={{ position: 'relative' }}>
          <div className={clsx('glide__track')} data-glide-el="track">
            <CarouselList
              data-variation={variation}
              data-cardsperview-tablet={noOfCardsTablet}
              data-cardsperview-desktop={noOfCards}
              className={clsx('glide__slides list-container pl-0 list-unstyled serverStyles')}
              id={`${componentId}__slides`}
            >
              {isAuthorMode && <CarouselSlidesInAuthorMode {...{ pagePath, itemPath, totalNumberOfCards }} />}
              {!isAuthorMode && <CarouselComponents {...{ totalNumberOfCards, cqItems, variation }} />}
            </CarouselList>
          </div>
          {!isAuthorMode && (
            <CarouselControls
              {...{
                componentId,
                prevLabel,
                updatedCardCount,
                cardsPerView,
                nextLabel,
                variation,
                isAutoplayPaused,
                onAutoplayToggle: handleAutoplayToggle,
              }}
            />
          )}
        </div>
      </div>
    </StyledMerchandisingCarouselContainer>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CarouselComponents = ({ totalNumberOfCards = [], cqItems, variation }: any) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchCardComponent = (cardName: string, jsonData: any, index: number) => {
    if (Object?.prototype?.hasOwnProperty?.call(jsonData, cardName)) {
      const card = jsonData[cardName];
      const gridUtil = respGridUtil({ ...card });

      return (
        <li
          className={clsx('glide__slide', `${CAROUSEL.ITEM} ${CAROUSEL.LOADER} ${variation}`)}
          id={`glide__slide__${cardName + index}`}
          key={cardName + index}
        >
          <ResponsiveGrid {...gridUtil} />
        </li>
      );
    }
    return null;
  };

  return useMemo(
    () => totalNumberOfCards?.map((cardName: string, index: number) => fetchCardComponent(cardName, cqItems, index)),
    [totalNumberOfCards?.length, JSON.stringify(cqItems)]
  );
};

const CarouselSlidesInAuthorMode = (props: ICarouselSlidesInAuthorMode) => {
  const { pagePath, itemPath, totalNumberOfCards } = props;
  return (
    <>
      {totalNumberOfCards?.map((compName: string) => (
        <ResponsiveGrid
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          pagePath={pagePath}
          itemPath={`${itemPath}/${compName}`}
          columnCount="12"
          gridClassNames={''}
          config={{
            isEmpty: () => true,
            resourceType: 'mi-aem-common-spa/components/container',
          }}
        />
      ))}
    </>
  );
};

const CarouselControls = (props: ICarouselControls) => {
  const {
    componentId,
    prevLabel,
    updatedCardCount,
    cardsPerView,
    nextLabel,
    variation,
    onAutoplayToggle,
    isAutoplayPaused,
  } = props;
  const { width = 0 } = useWindowSize();
  const isPostCardHeroVariation = variation == CAROUSEL_VARIATION.POST_CARD_HERO;
  const isTileSkinnyVariation = variation == CAROUSEL_VARIATION.TILE_SKINNY;
  const isTileVariation =
    variation == CAROUSEL_VARIATION.TILE_SQUARE ||
    variation == CAROUSEL_VARIATION.TILE_WIDE ||
    variation == CAROUSEL_VARIATION.MERCHANDISING_DCA_WRAPPER ||
    variation == CAROUSEL_VARIATION.PARTNER_CARD;

  return (
    <div
      className={clsx('center-align d-flex justify-content-center', {
        'invisible ': Number(updatedCardCount) <= Number(cardsPerView) && !isPostCardHeroVariation,
      })}
    >
      <div
        className={clsx({
          'carouselControlType2 ': isTileSkinnyVariation || (isPostCardHeroVariation && width < 768),
          'carouselControlType1 ': isTileVariation || (isPostCardHeroVariation && width >= 768),
        })}
      >
        <div className="glide__arrows" data-glide-el="controls">
          <button
            className={clsx('left-arrow', 'glide__arrow--left', 'mr-2 mr-lg-0', {
              '.is-disabled glide__arrow--disabled': !isPostCardHeroVariation,
            })}
            id={`${componentId}_left_arrow`}
            data-glide-dir="<"
            data-content={prevLabel}
            aria-label={prevLabel}
          >
            <span className="icon-arrow-left" data-id="left"></span>
          </button>
        </div>
        <div data-glide-el="controls[nav]" className="carousel__dots">
          {Array.from(
            {
              length: updatedCardCount - (cardsPerView - 1),
            },
            (_, i) => (
              <button
                className={clsx({ 'glide__bullet--active': i == 0 })}
                data-glide-dir={i}
                key={cardsPerView + i}
                aria-label={`glide-bullet-${i}`}
              ></button>
            )
          )}
        </div>
        <div className="glide__arrows" data-glide-el="controls">
          <button
            className={clsx('right-arrow', 'glide__arrow--right', 'ml-2 ml-lg-0')}
            id={`${componentId}_right_arrow`}
            data-glide-dir=">"
            data-content={nextLabel}
            aria-label={nextLabel}
          >
            <span className="icon-arrow-right" data-id="right"></span>
          </button>
        </div>
        {isPostCardHeroVariation && (
          <button
            type="button"
            className="carousel__autoplay-toggle d-none d-md-flex justify-content-center align-items-center ml-2"
            onClick={onAutoplayToggle}
            aria-label={isAutoplayPaused ? 'play carousel autoplay' : 'pause carousel autoplay'}
          >
            <span className={clsx('t-font-m', isAutoplayPaused ? 'icon-play' : 'icon-pause')}></span>
          </button>
        )}
      </div>
    </div>
  );
};

const getCarouselList = (variation: CAROUSEL_VARIATION) => {
  switch (variation) {
    case CAROUSEL_VARIATION.TILE_SQUARE:
    case CAROUSEL_VARIATION.TILE_WIDE:
    case CAROUSEL_VARIATION.MERCHANDISING_DCA_WRAPPER:
    case CAROUSEL_VARIATION.PARTNER_CARD:
      return StyledCarouselList;

    case CAROUSEL_VARIATION.POST_CARD_HERO:
      return StyledPostCardHeroCarouselList;
    default:
      return StyledCarouselList;
  }
};

const CarouselContainerConfig = {
  emptyLabel: 'Tile Carousel Container',
  isEmpty: () => true,
  resourceType: ``,
};

/**
 * @description this component is wrapped with EditableContainer
 * @param props
 * @returns
 */
export const MerchandisingCarouselContainer = (props: Record<string, unknown>) => {
  return (
    <EditableComponent config={CarouselContainerConfig} {...props}>
      <MerchandisingCarouselContainerNonEditable {...(props as MerchandisingCarouselContainerProps)} />
    </EditableComponent>
  );
};
